import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { rdSubcontractApi, FJ610, FJ611 } from '../../../services/rdSubcontractApi';

export type Mode = 'browse' | 'add' | 'edit';

const emptyDetail = (): Partial<FJ611> => ({
  fj611_02: 0, fj611_11: '', fj611_111: '', fj611_12: '', fj611_121: '',
  fj611_12a: '', fj611_14: 0, fj611_15: 0, fj611_151: 0,
  fj611_16: 0, fj611_161: 0, fj611_17: '',
});

export function useRdSubcontractForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FJ610[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Master form fields
  const [orderNo, setOrderNo] = useState('');
  const [processDate, setProcessDate] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [subcontractorCode, setSubcontractorCode] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [packingCode, setPackingCode] = useState('');
  const [unit, setUnit] = useState('');
  const [pricingMode, setPricingMode] = useState(0);
  const [currency, setCurrency] = useState('NTD');
  const [processingType, setProcessingType] = useState(0);
  const [weavingSubtype, setWeavingSubtype] = useState(0);
  const [remarkCode, setRemarkCode] = useState('');
  const [remarkContent, setRemarkContent] = useState('');
  const [memo, setMemo] = useState('');
  const [totalQty, setTotalQty] = useState(0);
  const [totalAmt, setTotalAmt] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [status, setStatus] = useState('N');
  const [detailSpec, setDetailSpec] = useState('');

  // Detail rows
  const [details, setDetails] = useState<Partial<FJ611>[]>([]);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rdSubcontractApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setOrderNo(current.fj610_01);
      setProcessDate(current.fj610_11 ? current.fj610_11.substring(0, 10) : '');
      setCompanyCode(current.fj610_12);
      setSubcontractorCode(current.fj610_14);
      setDeliveryDate(current.fj610_15 ? current.fj610_15.substring(0, 10) : '');
      setPackingCode(current.fj610_16);
      setUnit(current.fj610_21);
      setPricingMode(current.fj610_24);
      setCurrency(current.fj610_30);
      setProcessingType(current.fj610_48);
      setWeavingSubtype(current.fj610_50);
      setRemarkCode(current.fj610_18);
      setRemarkContent(current.fj610_19);
      setMemo(current.fj610_20);
      setTotalQty(current.fj610_17);
      setTotalAmt(current.fj610_22);
      setTotalItems(current.fj610_23);
      setStatus(current.fj610_80);
      setDetails(current.details || []);
      // Show first detail's spec if available
      const dets = current.details || [];
      setDetailSpec(dets.length > 0 ? (dets[0].fj611_17 || '') : '');
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Clear form for add
  const clearForm = () => {
    setOrderNo(''); setProcessDate(new Date().toISOString().substring(0, 10));
    setCompanyCode(''); setSubcontractorCode(''); setDeliveryDate('');
    setPackingCode(''); setUnit(''); setPricingMode(0);
    setCurrency('NTD'); setProcessingType(0); setWeavingSubtype(0);
    setRemarkCode(''); setRemarkContent(''); setMemo('');
    setTotalQty(0); setTotalAmt(0); setTotalItems(0); setStatus('N');
    setDetailSpec('');
    setDetails([emptyDetail()]);
  };

  const startAdd = () => { setMode('add'); clearForm(); };

  const startEdit = () => {
    if (!current) return;
    if (current.fj610_80 !== 'N' && current.fj610_80 !== '') {
      message.warning('此記錄已審核，不可修改');
      return;
    }
    setMode('edit');
    setOrderNo(current.fj610_01);
    setProcessDate(current.fj610_11 ? current.fj610_11.substring(0, 10) : '');
    setCompanyCode(current.fj610_12);
    setSubcontractorCode(current.fj610_14);
    setDeliveryDate(current.fj610_15 ? current.fj610_15.substring(0, 10) : '');
    setPackingCode(current.fj610_16);
    setUnit(current.fj610_21);
    setPricingMode(current.fj610_24);
    setCurrency(current.fj610_30);
    setProcessingType(current.fj610_48);
    setWeavingSubtype(current.fj610_50);
    setRemarkCode(current.fj610_18);
    setRemarkContent(current.fj610_19);
    setMemo(current.fj610_20);
    setDetails(current.details || []);
  };

  const cancel = () => { setMode('browse'); };

  // Detail row management
  const addDetailRow = () => {
    if (details.length >= 99) { message.warning('明細最多99行'); return; }
    setDetails([...details, emptyDetail()]);
  };

  const removeDetailRow = (idx: number) => {
    setDetails(details.filter((_, i) => i !== idx));
  };

  const updateDetail = (idx: number, field: string, value: string | number) => {
    const updated = [...details];
    updated[idx] = { ...updated[idx], [field]: value };
    setDetails(updated);
  };

  // Build request payload
  const buildPayload = () => ({
    master: {
      fj610_11: processDate || null,
      fj610_12: companyCode,
      fj610_14: subcontractorCode,
      fj610_15: deliveryDate || null,
      fj610_16: packingCode,
      fj610_21: unit,
      fj610_24: pricingMode,
      fj610_30: currency,
      fj610_48: processingType,
      fj610_50: weavingSubtype,
      fj610_18: remarkCode,
      fj610_19: remarkContent,
      fj610_20: memo,
    },
    details: details.map((d, i) => ({
      ...d,
      fj611_02: i + 1,
    })),
  });

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        const result = await rdSubcontractApi.create(buildPayload());
        message.success('新增成功');
        const data = await loadRecords();
        const idx = data.findIndex(r => r.fj610_01 === result.fj610_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else {
        await rdSubcontractApi.update(current!.fj610_01, buildPayload());
        message.success('修改成功');
        const data = await loadRecords();
        const idx = data.findIndex(r => r.fj610_01 === current!.fj610_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      }
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await rdSubcontractApi.delete(current.fj610_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r =>
      r.fj610_01.includes(searchText) || r.fj610_12.includes(searchText)
    );
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading, records, current, currentIdx,
    orderNo, processDate, setProcessDate,
    companyCode, setCompanyCode, subcontractorCode, setSubcontractorCode,
    deliveryDate, setDeliveryDate, packingCode, setPackingCode,
    unit, setUnit, pricingMode, setPricingMode,
    currency, setCurrency, processingType, setProcessingType,
    weavingSubtype, setWeavingSubtype,
    remarkCode, setRemarkCode, remarkContent, setRemarkContent,
    memo, setMemo, totalQty, totalAmt, totalItems, status,
    detailSpec, setDetailSpec,
    details, addDetailRow, removeDetailRow, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch, loadRecords,
  };
}
